/**
 * @author      sam@zumper.com (Sam Sherman)
 * @copyright   Copyright (c) 2019, Zumper
 * @description track optimizely events after sending message
 */

import type { Dispatch, Listable, Property } from '@zcode/state-type'
import * as Optimizely from '../../../packages/optimizely-middleware'
import {
  DIRECT_DEAL_LEADS_SENT,
  DIRECT_DEAL_MESSAGE_SENT,
  DIRECT_DEAL_MULTI_MESSAGE_SENT,
  DIRECT_DEAL_SINGLE_MESSAGE_SENT,
  LEAD_SENT,
  MESSAGE_SENT,
  MONETIZED_MESSAGE_SENT,
  MONETIZED_MULTI_MESSAGE_SENT,
  MONETIZED_SINGLE_MESSAGE_SENT,
  MULTI_MESSAGE_SENT,
  SELECT_EXCLUSIVE_MESSAGE_SENT,
  SELECT_EXCLUSIVE_MULTI_MESSAGE_SENT,
  SELECT_EXCLUSIVE_SINGLE_MESSAGE_SENT,
  SINGLE_MESSAGE_SENT,
  THIRD_PARTY_MESSAGE_SENT,
  THIRD_PARTY_MULTI_MESSAGE_SENT,
  THIRD_PARTY_SINGLE_MESSAGE_SENT,
} from '../../../thirdParty/optimizely/constants'

const trackOptimizelyMessageEvents =
  ({
    entity,
    isMulti,
    multiListables,
  }: {
    entity: Listable | Property
  } & (
    | {
        isMulti?: false
        multiListables?: never
      }
    | {
        isMulti: true
        multiListables: readonly Listable[]
      }
  )) =>
  (dispatch: Dispatch) => {
    if (!isMulti && entity) {
      const { isDirectDeal, isExclusive, isMonetizedM3, isThirdPartyPaid } = entity
      dispatch(Optimizely.track({ eventKey: LEAD_SENT }))
      dispatch(Optimizely.track({ eventKey: MESSAGE_SENT }))
      dispatch(Optimizely.track({ eventKey: SINGLE_MESSAGE_SENT }))

      if (isMonetizedM3) {
        dispatch(Optimizely.track({ eventKey: MONETIZED_MESSAGE_SENT }))
        dispatch(Optimizely.track({ eventKey: MONETIZED_SINGLE_MESSAGE_SENT }))
      }
      if (isExclusive) {
        // TODO: select is dead, change this SELECT_* const
        dispatch(Optimizely.track({ eventKey: SELECT_EXCLUSIVE_MESSAGE_SENT }))
        dispatch(Optimizely.track({ eventKey: SELECT_EXCLUSIVE_SINGLE_MESSAGE_SENT }))
      }
      if (isDirectDeal) {
        dispatch(Optimizely.track({ eventKey: DIRECT_DEAL_MESSAGE_SENT }))
        dispatch(Optimizely.track({ eventKey: DIRECT_DEAL_SINGLE_MESSAGE_SENT }))
        dispatch(Optimizely.track({ eventKey: DIRECT_DEAL_LEADS_SENT }))
      }
      if (isThirdPartyPaid) {
        dispatch(Optimizely.track({ eventKey: THIRD_PARTY_MESSAGE_SENT }))
        dispatch(Optimizely.track({ eventKey: THIRD_PARTY_SINGLE_MESSAGE_SENT }))
      }
    } else if (isMulti && Array.isArray(multiListables)) {
      multiListables.forEach((listable) => {
        const { isExclusive, isDirectDeal, isMonetizedM3, isThirdPartyPaid } = listable
        dispatch(Optimizely.track({ eventKey: LEAD_SENT }))
        dispatch(Optimizely.track({ eventKey: MESSAGE_SENT }))
        dispatch(Optimizely.track({ eventKey: MULTI_MESSAGE_SENT }))

        if (isMonetizedM3) {
          dispatch(Optimizely.track({ eventKey: MONETIZED_MESSAGE_SENT }))
          dispatch(Optimizely.track({ eventKey: MONETIZED_MULTI_MESSAGE_SENT }))
        }
        if (isExclusive) {
          dispatch(Optimizely.track({ eventKey: SELECT_EXCLUSIVE_MESSAGE_SENT }))
          dispatch(Optimizely.track({ eventKey: SELECT_EXCLUSIVE_MULTI_MESSAGE_SENT }))
        }
        if (isDirectDeal) {
          dispatch(Optimizely.track({ eventKey: DIRECT_DEAL_MESSAGE_SENT }))
          dispatch(Optimizely.track({ eventKey: DIRECT_DEAL_MULTI_MESSAGE_SENT }))
        }
        if (isThirdPartyPaid) {
          dispatch(Optimizely.track({ eventKey: THIRD_PARTY_MESSAGE_SENT }))
          dispatch(Optimizely.track({ eventKey: THIRD_PARTY_MULTI_MESSAGE_SENT }))
        }
      })
    }
  }

export default trackOptimizelyMessageEvents
